/* =========================================================================
   Tech-Life 2026 — theme.js
   Vanilla, no framework, deferred. Exposes window.TL with utilities used
   by every section that arrives in later phases.
   ========================================================================= */
(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const debounce = (fn, ms = 150) => {
    let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  };
  const throttle = (fn, ms = 100) => {
    let last = 0; return (...a) => {
      const now = Date.now();
      if (now - last >= ms) { last = now; fn(...a); }
    };
  };

  const announce = (msg) => {
    const live = document.getElementById('tl-live');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = msg; });
  };

  const focusTrap = (container) => {
    const focusables = $$(
      'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
      container
    );
    if (!focusables.length) return () => {};
    const first = focusables[0], last = focusables[focusables.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------- Cart (Shopify AJAX) ---------- */
  const Cart = {
    routes: {
      add:    '/cart/add.js',
      change: '/cart/change.js',
      update: '/cart/update.js',
      get:    '/cart.js'
    },

    async _post(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ description: 'Cart error' }));
        throw new Error(err.description || err.message || 'Cart error');
      }
      return res.json();
    },

    async get() {
      const res = await fetch(this.routes.get, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Cart error');
      return res.json();
    },

    async add(variantId, quantity = 1, properties = {}) {
      const item = await this._post(this.routes.add, {
        items: [{ id: variantId, quantity, properties }]
      });
      const cart = await this.get();
      this._notify(cart);
      announce('Added to cart');
      Drawer.open('#tl-cart-drawer');
      return { item, cart };
    },

    async addMultiple(items) {
      const item = await this._post(this.routes.add, { items });
      const cart = await this.get();
      this._notify(cart);
      announce('Added to cart');
      Drawer.open('#tl-cart-drawer');
      return { item, cart };
    },

    async update(line, quantity) {
      const cart = await this._post(this.routes.change, { line, quantity });
      this._notify(cart);
      return cart;
    },

    async remove(line) {
      return this.update(line, 0);
    },

    async updateNote(note) {
      const cart = await this._post(this.routes.update, { note });
      this._notify(cart);
      return cart;
    },

    _listeners: new Set(),
    onUpdate(fn) { this._listeners.add(fn); return () => this._listeners.delete(fn); },
    _notify(cart) {
      this._listeners.forEach(fn => { try { fn(cart); } catch (e) { console.error(e); } });
      document.dispatchEvent(new CustomEvent('tl:cart:update', { detail: cart }));
    }
  };

  /* ---------- Drawer (right-side or left-side) ---------- */
  const Drawer = {
    _open: null,
    _release: null,
    open(selector) {
      const el = typeof selector === 'string' ? $(selector) : selector;
      if (!el) return;
      this.close();
      const scrim = el.previousElementSibling?.classList?.contains('drawer-scrim')
        ? el.previousElementSibling
        : null;
      el.classList.add('is-open');
      scrim?.classList.add('is-open');
      document.body.classList.add('no-scroll');
      this._open = el;
      this._release = focusTrap(el);
      const closeBtn = $('[data-drawer-close]', el);
      (closeBtn || el).focus();
      // ESC to close
      this._esc = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this._esc);
      // Scrim click to close
      if (scrim) scrim.addEventListener('click', () => this.close(), { once: true });
    },
    close() {
      if (!this._open) return;
      const el = this._open;
      const scrim = el.previousElementSibling?.classList?.contains('drawer-scrim')
        ? el.previousElementSibling
        : null;
      el.classList.remove('is-open');
      scrim?.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      if (this._release) this._release();
      document.removeEventListener('keydown', this._esc);
      this._open = null; this._release = null; this._esc = null;
    }
  };

  /* ---------- Modal ---------- */
  const Modal = {
    _open: null, _release: null,
    open(selector) {
      const el = typeof selector === 'string' ? $(selector) : selector;
      if (!el) return;
      this.close();
      el.classList.add('is-open');
      document.body.classList.add('no-scroll');
      this._open = el;
      this._release = focusTrap(el);
      ($('[data-modal-close]', el) || el).focus();
      this._esc = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this._esc);
      el.addEventListener('click', (e) => { if (e.target === el) this.close(); }, { once: true });
    },
    close() {
      if (!this._open) return;
      this._open.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      if (this._release) this._release();
      document.removeEventListener('keydown', this._esc);
      this._open = null; this._release = null; this._esc = null;
    }
  };

  /* ---------- Reveal on scroll ---------- */
  const Reveal = {
    init() {
      if (!document.body.classList.contains('anim-on')) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        $$('.reveal').forEach(el => el.classList.add('is-in'));
        return;
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.04 });
      $$('.reveal').forEach(el => io.observe(el));
    }
  };

  /* ---------- Tabs (ARIA) ---------- */
  const Tabs = {
    init(root = document) {
      $$('[data-tabs]', root).forEach(group => {
        const tabs   = $$('[role="tab"]', group);
        const panels = $$('[role="tabpanel"]', group);
        const select = (idx) => {
          tabs.forEach((t, i) => {
            const on = i === idx;
            t.setAttribute('aria-selected', on);
            t.setAttribute('tabindex', on ? '0' : '-1');
            panels[i].hidden = !on;
          });
        };
        tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => select(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); select((i + 1) % tabs.length); tabs[(i + 1) % tabs.length].focus(); }
            if (e.key === 'ArrowLeft')  { e.preventDefault(); const p = (i - 1 + tabs.length) % tabs.length; select(p); tabs[p].focus(); }
          });
        });
      });
    }
  };

  /* ---------- Accordion ---------- */
  const Accordion = {
    init(root = document) {
      $$('[data-accordion]', root).forEach(acc => {
        $$('.accordion__btn', acc).forEach(btn => {
          btn.setAttribute('aria-expanded', btn.getAttribute('aria-expanded') || 'false');
          // When the button is wrapped in <h3>/<h4>, nextElementSibling is null.
          // Look up via the closest .accordion__item wrapper, then fall back to
          // the direct-sibling pattern used by menu-drawer.
          const item = btn.closest('.accordion__item');
          const panel = (item && item.querySelector('.accordion__panel')) || btn.nextElementSibling;
          btn.addEventListener('click', () => {
            const expanded = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', String(!expanded));
            if (panel) panel.classList.toggle('is-open', !expanded);
          });
        });
      });
    }
  };

  /* ---------- Support hub tabs (exclusive panel reveal) ---------- */
  const SupportTabs = {
    init(root = document) {
      $$('[data-support-tabs]', root).forEach(tablist => {
        const tabs = $$('[role="tab"]', tablist);
        if (!tabs.length) return;
        const panelFor = (tab) => document.getElementById(tab.getAttribute('aria-controls'));

        // nextTab === null closes every panel (nothing open by default).
        const select = (nextTab) => {
          tabs.forEach(t => {
            const on = t === nextTab;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            t.setAttribute('tabindex', on ? '0' : '-1');
            const panel = panelFor(t);
            if (panel) panel.classList.toggle('is-open', on);
          });
          // Keep one keyboard entry point when no panel is open.
          if (!nextTab && tabs[0]) tabs[0].setAttribute('tabindex', '0');
        };

        tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => {
            // Toggle: clicking the open tab collapses it back to the closed state.
            const isOpen = tab.getAttribute('aria-selected') === 'true';
            select(isOpen ? null : tab);
          });
          tab.addEventListener('keydown', (e) => {
            const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            const next = tabs[(i + dir + tabs.length) % tabs.length];
            next.focus();
            select(next);
          });
        });

        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-support-tab-go]');
          if (!trigger) return;
          const targetId = trigger.getAttribute('data-support-tab-go');
          const targetTab = tabs.find(t => t.id === targetId);
          if (!targetTab) return;
          e.preventDefault();
          select(targetTab);
          targetTab.focus();
          tablist.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }
  };

  /* ---------- Carousel (snap-scroll w/ prev/next buttons) ---------- */
  const Carousel = {
    init(root = document) {
      $$('[data-carousel]', root).forEach(c => {
        const track = $('[data-carousel-track]', c);
        const prev  = $('[data-carousel-prev]', c);
        const next  = $('[data-carousel-next]', c);
        if (!track) return;
        const step = () => track.clientWidth * 0.85;
        prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
        next?.addEventListener('click', () => track.scrollBy({ left:  step(), behavior: 'smooth' }));
      });
    }
  };

  /* ---------- Stacked slider (centered card-stack w/ tilted side cards) ----------
     Markup contract (see sections/product-highlight-slider.liquid):
       [data-stacked-slider] > [data-stacked-track] > [data-stacked-slide]*
       any number of [data-stacked-prev] / [data-stacked-next] buttons inside root
       optional [data-stacked-progress] bar, optional data-autoplay-interval (ms).
     JS only swaps is-active / is-prev / is-next classes; CSS owns the motion. */
  const StackedSlider = {
    init(root = document) {
      $$('[data-stacked-slider]', root).forEach(el => {
        if (el.dataset.stackedBound) return;
        el.dataset.stackedBound = 'true';

        const track  = $('[data-stacked-track]', el);
        const slides = $$('[data-stacked-slide]', el);
        const progress = $('[data-stacked-progress]', el);
        const n = slides.length;
        if (!track || !n) return;

        const baseLabel = track.getAttribute('aria-label') || 'Carousel';
        const interval  = parseInt(el.getAttribute('data-autoplay-interval') || '0', 10);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let index = 0;
        let timer = null;
        let paused = false;

        const render = () => {
          const prevIdx = (index - 1 + n) % n;
          const nextIdx = (index + 1) % n;
          slides.forEach((s, i) => {
            const active = i === index;
            const isPrev = !active && i === prevIdx;
            const isNext = !active && !isPrev && i === nextIdx;
            s.classList.toggle('is-active', active);
            s.classList.toggle('is-prev', isPrev);
            s.classList.toggle('is-next', isNext);
            s.setAttribute('aria-hidden', String(!active));
          });
          if (progress) {
            progress.style.width = (100 / n) + '%';
            progress.style.transform = `translateX(${index * 100}%)`;
          }
          track.setAttribute('aria-label', `${baseLabel} — slide ${index + 1} of ${n}`);
        };

        const stop  = () => { if (timer) { clearInterval(timer); timer = null; } };
        const start = () => {
          if (!interval || n < 2 || paused || timer || reduceMotion.matches) return;
          timer = setInterval(() => { index = (index + 1) % n; render(); }, interval);
        };
        const goTo = (i) => { index = (i + n) % n; render(); stop(); start(); };

        $$('[data-stacked-prev]', el).forEach(b => b.addEventListener('click', () => goTo(index - 1)));
        $$('[data-stacked-next]', el).forEach(b => b.addEventListener('click', () => goTo(index + 1)));

        track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(index - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
        });

        let touchX = null;
        track.addEventListener('touchstart', (e) => {
          touchX = e.changedTouches[0].clientX;
          stop();
        }, { passive: true });
        track.addEventListener('touchend', (e) => {
          if (touchX === null) return;
          const dx = e.changedTouches[0].clientX - touchX;
          touchX = null;
          if (Math.abs(dx) > 40) goTo(index + (dx < 0 ? 1 : -1));
          else start();
        }, { passive: true });

        el.addEventListener('mouseenter', () => { paused = true;  stop(); });
        el.addEventListener('mouseleave', () => { paused = false; start(); });
        el.addEventListener('focusin',    () => { paused = true;  stop(); });
        el.addEventListener('focusout',   (e) => {
          if (el.contains(e.relatedTarget)) return;
          paused = false; start();
        });

        render();
        start();
      });

      // Rebind after Customizer section re-renders (no-op on the storefront).
      if (!this._editorBound) {
        this._editorBound = true;
        document.addEventListener('shopify:section:load', (e) => this.init(e.target));
      }
    }
  };

  /* ---------- Header (transparent → opaque on scroll, mega-nav) ---------- */
  const Header = {
    init() {
      const header = $('[data-header]');
      if (!header) return;
      const update = () => {
        const scrolled = window.scrollY > 8;
        header.classList.toggle('is-scrolled', scrolled);
      };
      update();
      window.addEventListener('scroll', throttle(update, 60), { passive: true });
    }
  };

  /* ---------- MultiLink teaser hotspots (click number → drop-down panel) ---------- */
  const MLTeaser = {
    closeAll(except) {
      $$('[data-ml-pin][aria-expanded="true"]').forEach(p => {
        if (p === except) return;
        p.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(p.getAttribute('aria-controls'));
        if (panel) panel.classList.remove('is-open');
      });
    },
    init() {
      // Event-delegated so it survives Customizer section re-renders.
      document.addEventListener('click', (e) => {
        const pin = e.target.closest('[data-ml-pin]');
        if (pin) {
          const panel = document.getElementById(pin.getAttribute('aria-controls'));
          if (!panel) return;
          const open = pin.getAttribute('aria-expanded') === 'true';
          this.closeAll(pin);
          pin.setAttribute('aria-expanded', String(!open));
          panel.classList.toggle('is-open', !open);
          return;
        }
        if (!e.target.closest('.ml-teaser__pop')) this.closeAll();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = $('[data-ml-pin][aria-expanded="true"]');
        if (open) { this.closeAll(); open.focus(); }
      });
    }
  };

  /* ---------- Auto-bind common triggers ---------- */
  const bindTriggers = () => {
    document.addEventListener('click', (e) => {
      const drawerOpen  = e.target.closest('[data-drawer-open]');
      const drawerClose = e.target.closest('[data-drawer-close]');
      const modalOpen   = e.target.closest('[data-modal-open]');
      const modalClose  = e.target.closest('[data-modal-close]');
      const ajaxAdd     = e.target.closest('[data-ajax-add]');

      if (drawerOpen)  { e.preventDefault(); Drawer.open(drawerOpen.getAttribute('data-drawer-open')); }
      if (drawerClose) { e.preventDefault(); Drawer.close(); }
      if (modalOpen)   { e.preventDefault(); Modal.open(modalOpen.getAttribute('data-modal-open')); }
      if (modalClose)  { e.preventDefault(); Modal.close(); }

      if (ajaxAdd) {
        e.preventDefault();
        const id = ajaxAdd.getAttribute('data-variant-id');
        const qty = parseInt(ajaxAdd.getAttribute('data-quantity') || '1', 10);
        if (!id) return;
        if (ajaxAdd.getAttribute('aria-busy') === 'true') return; // block double-submit
        ajaxAdd.setAttribute('aria-busy', 'true');
        Cart.add(parseInt(id, 10), qty)
          .catch(err => { console.error(err); announce(err.message || 'Could not add to cart'); })
          .finally(() => ajaxAdd.removeAttribute('aria-busy'));
      }
    });
  };

  /* ---------- Init ---------- */
  const init = () => {
    Header.init();
    Reveal.init();
    Tabs.init();
    Accordion.init();
    SupportTabs.init();
    Carousel.init();
    StackedSlider.init();
    MLTeaser.init();
    bindTriggers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  /* =========================================================================
     PHASE 2 - Header system + Cart drawer rendering
     ========================================================================= */

  /* ---------- Announcement bar rotator ---------- */
  const Announcement = {
    init() {
      $$('[data-announcement]').forEach(bar => {
        if (bar.hasAttribute('data-dismissible') && sessionStorage.getItem('tl_ann_dismissed') === '1') {
          bar.style.display = 'none'; return;
        }
        const msgs = $$('.announcement__msg', bar);
        if (msgs.length < 2) return;
        let i = 0;
        const ms = parseInt(bar.getAttribute('data-rotate-ms') || '5000', 10);
        const tick = () => {
          msgs[i].setAttribute('aria-hidden', 'true');
          msgs[i].setAttribute('hidden', '');
          i = (i + 1) % msgs.length;
          msgs[i].removeAttribute('aria-hidden');
          msgs[i].removeAttribute('hidden');
        };
        let timer = setInterval(tick, ms);
        bar.addEventListener('mouseenter', () => clearInterval(timer));
        bar.addEventListener('mouseleave', () => { timer = setInterval(tick, ms); });
        const dismiss = $('[data-announcement-dismiss]', bar);
        if (dismiss) dismiss.addEventListener('click', () => {
          sessionStorage.setItem('tl_ann_dismissed', '1');
          bar.style.display = 'none';
          clearInterval(timer);
        });
      });
    }
  };

  /* ---------- Mega-nav ---------- */
  const MegaNav = {
    init() {
      $$('.header__nav-item.has-mega').forEach(item => {
        const trigger = $('[data-mega-trigger]', item);
        const panel   = $('[data-mega-panel]', item);
        if (!trigger || !panel) return;
        let openTimer = null, closeTimer = null;
        const open = () => {
          clearTimeout(closeTimer);
          $$('.header__nav-item.is-open').forEach(o => { if (o !== item) o.classList.remove('is-open'); });
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          panel.removeAttribute('hidden');
        };
        const close = () => {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          panel.setAttribute('hidden', '');
        };
        item.addEventListener('mouseenter', () => { clearTimeout(closeTimer); openTimer = setTimeout(open, 80); });
        item.addEventListener('mouseleave', () => { clearTimeout(openTimer); closeTimer = setTimeout(close, 200); });
        trigger.addEventListener('click', (e) => {
          if (window.matchMedia('(hover: none)').matches || e.detail === 0) {
            e.preventDefault();
            item.classList.contains('is-open') ? close() : open();
          }
        });
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { close(); trigger.focus(); }
          if (e.key === 'ArrowDown') { e.preventDefault(); open(); const first = $('a, button', panel); first && first.focus(); }
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) close();
        });
      });
    }
  };

  /* ---------- Search overlay ---------- */
  const Search = {
    init() {
      const overlay = $('[data-search-overlay]');
      if (!overlay) return;
      $$('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', () => {
          const isOpen = !overlay.hasAttribute('hidden');
          if (isOpen) {
            overlay.setAttribute('hidden', '');
            btn.setAttribute('aria-expanded', 'false');
          } else {
            overlay.removeAttribute('hidden');
            btn.setAttribute('aria-expanded', 'true');
            const input = $('input[type="search"]', overlay);
            if (input) input.focus();
          }
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !overlay.hasAttribute('hidden')) {
          overlay.setAttribute('hidden', '');
          $$('[data-search-toggle]').forEach(b => b.setAttribute('aria-expanded', 'false'));
        }
      });
    }
  };

  /* ---------- Money formatter ---------- */
  const Money = {
    _format: '${{amount}}',
    init() {
      if (window.Shopify && window.Shopify.money_format) this._format = window.Shopify.money_format;
    },
    format(cents) {
      const value = (cents / 100).toFixed(2);
      const parts = value.split('.');
      const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this._format
        .replace('{{amount}}', withCommas + '.' + parts[1])
        .replace('{{amount_no_decimals}}', withCommas)
        .replace('{{amount_with_comma_separator}}', withCommas + ',' + parts[1]);
    }
  };

  /* ---------- Cart drawer rendering ---------- */
  const CartUI = {
    drawer: null,
    threshold: 5000,
    upsellEnabled: true,
    upsellCount: 3,

    init() {
      this.drawer = $('#tl-cart-drawer');
      this.page   = $('[data-cart-page]');
      if (!this.drawer && !this.page) return;
      if (this.drawer) {
        this.threshold     = parseInt(this.drawer.getAttribute('data-free-shipping-threshold') || '5000', 10);
        this.upsellEnabled = this.drawer.getAttribute('data-cart-upsell-enabled') === '1';
        this.upsellCount   = parseInt(this.drawer.getAttribute('data-cart-upsell-count') || '3', 10);
        this.drawer.addEventListener('click',  (e) => this._onClick(e));
        this.drawer.addEventListener('change', (e) => this._onChange(e));
      }
      if (this.page) {
        this.page.addEventListener('click',  (e) => this._onClick(e));
        this.page.addEventListener('change', (e) => this._onChange(e));
        const note = $('[data-cart-note]', this.page);
        if (note) note.addEventListener('change', () => {
          Cart.updateNote(note.value)
            .then(() => announce('Order note saved'))
            .catch(err => announce(err.message));
        });
      }
      Cart.onUpdate((cart) => this.render(cart));
      Cart.get().then(c => this.render(c)).catch(() => {});
    },

    _onClick(e) {
      const dec   = e.target.closest('[data-qty-decrement]');
      const inc   = e.target.closest('[data-qty-increment]');
      const rm    = e.target.closest('[data-line-remove]');
      const upAdd = e.target.closest('[data-upsell-add]');
      if (dec || inc) {
        const li = e.target.closest('[data-cart-item]');
        const input = $('[data-qty-input]', li);
        const q = Math.max(0, parseInt(input.value || '0', 10) + (inc ? 1 : -1));
        input.value = q;
        this._update(li, q);
      }
      if (rm) {
        const li = e.target.closest('[data-cart-item]');
        this._update(li, 0);
      }
      if (upAdd) {
        const id = parseInt(upAdd.getAttribute('data-variant-id'), 10);
        if (id) {
          if (upAdd.getAttribute('aria-busy') === 'true') return; // block double-submit
          upAdd.setAttribute('aria-busy', 'true');
          Cart.add(id, 1)
            .catch(err => announce(err.message))
            .finally(() => upAdd.removeAttribute('aria-busy'));
        }
      }
    },

    _onChange(e) {
      const input = e.target.closest('[data-qty-input]');
      if (!input) return;
      const li = e.target.closest('[data-cart-item]');
      const q = Math.max(0, parseInt(input.value || '0', 10));
      this._update(li, q);
    },

    _update(li, qty) {
      const line = parseInt(li.getAttribute('data-line'), 10);
      li.setAttribute('aria-busy', 'true');
      Cart.update(line, qty)
        .catch(err => announce(err.message))
        .finally(() => li.removeAttribute('aria-busy'));
    },

    render(cart) {
      if (!cart) return;

      $$('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
      });

      if (this.drawer) this._renderDrawer(cart);
      if (this.page) this._renderPage(cart);
    },

    _renderDrawer(cart) {
      const subtotalEl = $('[data-cart-subtotal]', this.drawer);
      if (subtotalEl) subtotalEl.textContent = Money.format(cart.total_price);

      const foot = $('[data-cart-foot]', this.drawer);
      if (foot) {
        if (cart.item_count > 0) foot.removeAttribute('hidden');
        else foot.setAttribute('hidden', '');
      }

      const body = $('[data-cart-body]', this.drawer);
      const itemsEl = $('[data-cart-items]', this.drawer);
      if (cart.item_count === 0) {
        if (itemsEl) itemsEl.remove();
        if (body && !$('.cart-drawer__empty', body)) {
          const empty = document.createElement('div');
          empty.className = 'cart-drawer__empty';
          empty.innerHTML = '<p class="lead">Your cart is empty</p>' +
            '<a class="btn btn-primary" href="/collections/all" data-drawer-close>Continue shopping</a>';
          body.prepend(empty);
        }
      } else {
        const empty = $('.cart-drawer__empty', this.drawer);
        if (empty) empty.remove();
        let list = itemsEl;
        if (!list) {
          list = document.createElement('ul');
          list.className = 'cart-drawer__items';
          list.setAttribute('role', 'list');
          list.setAttribute('data-cart-items', '');
          body.prepend(list);
        }
        list.innerHTML = cart.items.map((it, i) => this._lineHTML(it, i + 1)).join('');
      }

      this._renderMeter(cart, this.drawer);
      if (this.upsellEnabled) this._renderUpsells(cart);
    },

    _renderPage(cart) {
      const subtotalEl = $('[data-cart-page-subtotal]', this.page);
      if (subtotalEl) subtotalEl.textContent = Money.format(cart.total_price);

      const checkoutPrice = $('[data-cart-checkout] .spec-value', this.page);
      if (checkoutPrice) checkoutPrice.textContent = Money.format(cart.total_price);

      const emptyMsg = $('[data-cart-empty]', this.page);
      const grid = $('.cart-page__grid', this.page);
      if (cart.item_count === 0) {
        if (emptyMsg) emptyMsg.removeAttribute('hidden');
        if (grid) grid.setAttribute('hidden', '');
        return;
      }
      if (emptyMsg) emptyMsg.setAttribute('hidden', '');
      if (grid) grid.removeAttribute('hidden');

      const list = $('[data-cart-page-items]', this.page);
      if (list) list.innerHTML = cart.items.map((it, i) => this._lineHTML(it, i + 1)).join('');

      this._renderMeter(cart, this.page);
    },

    _lineHTML(item, line) {
      const img = item.image
        ? '<img src="' + item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=176" alt="' + this._esc(item.product_title) + '" loading="lazy" width="88" height="88">'
        : '';
      const variant = (item.options_with_values && item.product_has_only_default_variant === false)
        ? '<p class="cart-item__variant fineprint">' + item.options_with_values.map(o => this._esc(o.value)).join(' / ') + '</p>'
        : '';
      const minusSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M5 12h14"/></svg>';
      const plusSvg  = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
      return '<li class="cart-item" data-cart-item data-line="' + line + '" data-key="' + item.key + '">' +
        '<a href="' + item.url + '" class="cart-item__media" data-drawer-close>' + img + '</a>' +
        '<div class="cart-item__body">' +
          '<a href="' + item.url + '" class="cart-item__title" data-drawer-close>' + this._esc(item.product_title) + '</a>' +
          variant +
          '<div class="cart-item__row">' +
            '<div class="qty-stepper" role="group" aria-label="Quantity">' +
              '<button class="qty-stepper__btn" type="button" data-qty-decrement aria-label="Decrease quantity">' + minusSvg + '</button>' +
              '<input class="qty-stepper__input spec-value" type="number" min="0" inputmode="numeric" value="' + item.quantity + '" data-qty-input aria-label="Quantity">' +
              '<button class="qty-stepper__btn" type="button" data-qty-increment aria-label="Increase quantity">' + plusSvg + '</button>' +
            '</div>' +
            '<p class="cart-item__price spec-value">' + Money.format(item.final_line_price) + '</p>' +
          '</div>' +
          '<button class="cart-item__remove" type="button" data-line-remove>Remove</button>' +
        '</div>' +
      '</li>';
    },

    _renderMeter(cart, root) {
      const wrap = $('[data-cart-meter]', root);
      if (!wrap || this.threshold <= 0) { if (wrap) wrap.style.display = 'none'; return; }
      const remaining = Math.max(0, this.threshold - cart.total_price);
      const pct = Math.min(100, Math.round(cart.total_price / this.threshold * 100));
      const unlocked = remaining === 0;
      const checkSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5 9-11"/></svg>';
      const label = unlocked
        ? '<span class="meter__icon" aria-hidden="true">' + checkSvg + '</span> You have unlocked free shipping'
        : 'You are ' + Money.format(remaining) + ' away from free shipping';
      wrap.innerHTML =
        '<div class="meter" data-meter-pct="' + pct + '">' +
          '<p class="meter__label">' + label + '</p>' +
          '<div class="meter__bar" role="progressbar" aria-valuenow="' + pct + '" aria-valuemin="0" aria-valuemax="100">' +
            '<span class="meter__fill" style="width:' + pct + '%"></span>' +
          '</div>' +
        '</div>';
    },

    _renderUpsells(cart) {
      const wrap = $('[data-cart-upsell]', this.drawer);
      const grid = $('[data-cart-upsell-grid]', this.drawer);
      if (!wrap || !grid || cart.item_count === 0) {
        if (wrap) wrap.setAttribute('hidden', '');
        return;
      }
      const baseId = cart.items[0] && cart.items[0].product_id;
      if (!baseId) return;
      fetch('/recommendations/products.json?product_id=' + baseId + '&limit=' + this.upsellCount + '&intent=related')
        .then(r => r.json())
        .then(data => {
          const products = ((data && data.products) || []).slice(0, this.upsellCount);
          const inCart = new Set(cart.items.map(i => i.product_id));
          const filtered = products.filter(p => !inCart.has(p.id));
          if (filtered.length === 0) { wrap.setAttribute('hidden', ''); return; }
          const plusSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
          grid.innerHTML = filtered.map(p => {
            const vid = p.variants && p.variants[0] && p.variants[0].id;
            return '<article class="cart-upsell-card">' +
              '<a href="' + p.url + '" data-drawer-close><img src="' + p.featured_image + '" alt="' + this._esc(p.title) + '" loading="lazy"></a>' +
              '<div class="cart-upsell-card__body">' +
                '<a class="cart-upsell-card__title" href="' + p.url + '" data-drawer-close>' + this._esc(p.title) + '</a>' +
                '<p class="cart-upsell-card__price spec-value">' + Money.format(p.price) + '</p>' +
              '</div>' +
              '<button class="cart-upsell-card__add" type="button" data-upsell-add data-variant-id="' + vid + '" aria-label="Add ' + this._esc(p.title) + ' to cart">' + plusSvg + '</button>' +
            '</article>';
          }).join('');
          wrap.removeAttribute('hidden');
        })
        .catch(() => wrap.setAttribute('hidden', ''));
    },

    _esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]);
    }
  };

  /* ---------- Re-init on DOMContentLoaded with Phase 2 augments ---------- */
  const initPhase2 = () => {
    Money.init();
    Announcement.init();
    MegaNav.init();
    Search.init();
    CartUI.init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhase2);
  } else {
    initPhase2();
  }

    /* =========================================================================
     PHASE 4 - Collection page + Quick Shop loader
     ========================================================================= */

  /* ---------- QuickShop (lazy-loaded mini-PDP modal) ---------- */
  const QuickShop = {
    _cache: new Map(),

    init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-shop-handle]');
        if (!trigger) return;
        e.preventDefault();
        const handle = trigger.getAttribute('data-quick-shop-handle');
        this.open(handle);
      });
    },

    async open(handle) {
      const modal = $('#tl-quick-shop');
      const body  = $('[data-quick-shop-body]', modal);
      if (!modal || !body) return;
      Modal.open(modal);
      body.innerHTML = '<div class="quick-shop__loading" aria-live="polite">Loading...</div>';

      try {
        let product;
        if (this._cache.has(handle)) {
          product = this._cache.get(handle);
        } else {
          const res = await fetch('/products/' + handle + '.js');
          if (!res.ok) throw new Error('Could not load product');
          product = await res.json();
          this._cache.set(handle, product);
        }
        body.innerHTML = this._render(product);
        this._wireVariantPicker(body, product);
      } catch (err) {
        body.innerHTML = '<p class="quick-shop__error">Could not load. <a href="/products/' + handle + '">View full page</a>.</p>';
      }
    },

    _render(p) {
      const sel = p.variants[0];
      const img = p.featured_image || (p.images && p.images[0]);
      const optionsHTML = p.options.map((opt, i) => {
        const optsHTML = (Array.from(new Set(p.variants.map(v => v.options[i])))).map(val => {
          return '<button type="button" class="qs-opt" data-option-index="' + i + '" data-value="' + this._esc(val) + '">' + this._esc(val) + '</button>';
        }).join('');
        return '<div class="qs-option"><p class="eyebrow eyebrow--muted">' + this._esc(opt) + '</p><div class="qs-opt-row" data-qs-opt-row>' + optsHTML + '</div></div>';
      }).join('');

      return '' +
        '<div class="quick-shop__grid">' +
          '<div class="quick-shop__media">' +
            (img ? '<img src="' + img + '" alt="' + this._esc(p.title) + '" loading="eager">' : '') +
          '</div>' +
          '<div class="quick-shop__detail">' +
            '<a class="quick-shop__viewlink" href="' + p.url + '">View full details &rarr;</a>' +
            '<h3 class="quick-shop__title">' + this._esc(p.title) + '</h3>' +
            '<p class="quick-shop__price spec-value" data-qs-price>' + Money.format(sel.price) + '</p>' +
            (p.description ? '<div class="quick-shop__desc fineprint">' + p.description.split(/<\/p>|<br\s*\/?>/i)[0].replace(/<[^>]*>/g, '').slice(0, 240) + '...</div>' : '') +
            optionsHTML +
            '<button class="btn btn-primary btn-block" type="button" data-qs-add data-variant-id="' + sel.id + '">Add to cart - <span data-qs-add-price>' + Money.format(sel.price) + '</span></button>' +
          '</div>' +
        '</div>';
    },

    _wireVariantPicker(body, product) {
      const selected = product.options.map((_, i) => product.variants[0].options[i]);

      const refreshOpts = () => {
        $$('[data-qs-opt-row]', body).forEach((row, i) => {
          $$('[data-option-index="' + i + '"]', row).forEach(btn => {
            btn.classList.toggle('is-selected', btn.getAttribute('data-value') === selected[i]);
          });
        });
      };

      const refreshVariant = () => {
        const v = product.variants.find(v => v.options.every((opt, i) => opt === selected[i]));
        const addBtn = $('[data-qs-add]', body);
        const priceEl = $('[data-qs-price]', body);
        const addPriceEl = $('[data-qs-add-price]', body);
        if (v) {
          if (priceEl) priceEl.textContent = Money.format(v.price);
          if (addPriceEl) addPriceEl.textContent = Money.format(v.price);
          addBtn.setAttribute('data-variant-id', v.id);
          if (v.available) {
            addBtn.disabled = false;
            addBtn.firstChild.textContent = 'Add to cart - ';
          } else {
            addBtn.disabled = true;
            addBtn.firstChild.textContent = 'Sold out';
            if (addPriceEl) addPriceEl.textContent = '';
          }
        }
      };

      body.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-option-index]');
        if (opt) {
          selected[parseInt(opt.getAttribute('data-option-index'), 10)] = opt.getAttribute('data-value');
          refreshOpts();
          refreshVariant();
        }
        const add = e.target.closest('[data-qs-add]');
        if (add && !add.disabled) {
          const id = parseInt(add.getAttribute('data-variant-id'), 10);
          if (add.getAttribute('aria-busy') === 'true') return; // block double-submit
          add.setAttribute('aria-busy', 'true');
          Cart.add(id, 1)
            .then(() => Modal.close())
            .catch(err => announce(err.message))
            .finally(() => add.removeAttribute('aria-busy'));
        }
      });

      refreshOpts();
      refreshVariant();
    },

    _esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]);
    }
  };

  /* ---------- Collection filters + sort (auto-submit) ---------- */
  const CollectionFilters = {
    init() {
      const sortForm = $('[data-collection-sort]');
      if (sortForm) {
        $('select', sortForm).addEventListener('change', () => this.submit(sortForm));
      }
      const filterForm = $('[data-collection-filters]');
      if (filterForm) {
        // Apply on submit (button); also live-update active count
        filterForm.addEventListener('change', () => this._updateCount());
        filterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submit(filterForm);
        });
        this._updateCount();
      }
    },

    submit(form) {
      const params = new URLSearchParams(new FormData(form));
      // Strip empty values
      Array.from(params.keys()).forEach(k => {
        if (!params.get(k)) params.delete(k);
      });
      const url = new URL(window.location.href);
      url.search = params.toString();
      window.location.assign(url.toString());
    },

    _updateCount() {
      const form = $('[data-collection-filters]');
      if (!form) return;
      const count = $$('input[type="checkbox"]:checked', form).length;
      $$('[data-active-filter-count]').forEach(el => {
        el.textContent = count > 0 ? '(' + count + ')' : '';
      });
    }
  };

  /* ---------- Wire Phase 4 inits into the existing init flow ---------- */
  const initPhase4 = () => {
    QuickShop.init();
    CollectionFilters.init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhase4);
  } else {
    initPhase4();
  }

    /* =========================================================================
     PHASE 5 - PDP interactions
     ========================================================================= */

  /* ---------- PDP gallery (thumbnail switching) ---------- */
  const PDPGallery = {
    init() {
      const gallery = $('[data-pdp-gallery]');
      if (!gallery) return;
      const main = $('[data-pdp-gallery-main] img', gallery);
      const thumbs = $$('[data-pdp-thumb]', gallery);

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const src = thumb.getAttribute('data-media-src');
          const srcset = thumb.getAttribute('data-media-srcset');
          if (main && src) {
            main.src = src;
            if (srcset) main.srcset = srcset;
            // The main img lives inside a <picture> whose WebP <source>
            // elements outrank img.src — update (or drop) them too, or the
            // visible image never changes.
            const picture = main.closest('picture');
            if (picture) {
              const webpSet = thumb.getAttribute('data-media-srcset-webp');
              picture.querySelectorAll('source').forEach(source => {
                if (source.type === 'image/webp' && webpSet) source.srcset = webpSet;
                else source.remove();
              });
            }
          }
          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    },

    showVariantImage(mediaId) {
      const thumb = $(`[data-pdp-thumb][data-media-id="${mediaId}"]`);
      if (thumb) thumb.click();
    },

    filterImages(colorName) {
      const colorTag = 'base-color_' + colorName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const gallery = $('[data-pdp-gallery]');
      if (!gallery) return;

      // Filter desktop grid items
      const gridItems = $$('[data-gallery-grid-item]', gallery);
      gridItems.forEach(item => {
        const tag = item.getAttribute('data-option-tag');
        if (!tag || tag === colorTag) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });

      // Filter mobile thumbnails
      const thumbItems = $$('[data-gallery-thumb-item]', gallery);
      thumbItems.forEach(item => {
        const tag = item.getAttribute('data-option-tag');
        if (!tag || tag === colorTag) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });

      // If the currently active thumbnail is hidden, find the first visible one and activate it
      const activeThumb = $('.pdp-gallery__thumb.is-active', gallery);
      if (activeThumb) {
        const activeItem = activeThumb.closest('[data-gallery-thumb-item]');
        if (activeItem && activeItem.style.display === 'none') {
          const firstVisibleThumb = $('[data-gallery-thumb-item]:not([style*="display: none"]) .pdp-gallery__thumb', gallery);
          if (firstVisibleThumb) {
            firstVisibleThumb.click();
          }
        }
      }
    }
  };

  /* ---------- PDP variant picker ---------- */
  const PDPVariantPicker = {
    variants: [],
    currentOptions: [],
    colorOptionIndex: -1,

    init() {
      const root = $('[data-variant-picker]');
      if (!root) return;
      const dataEl = $('[data-variants]', root);
      try { this.variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

      // Init currentOptions from selected pills
      const picker = $('.pdp__detail [data-variant-picker]') || root;
      this.currentOptions = $$('.pdp-variant', picker).map(opt => {
        const sel = $('.pdp-variant__pill.is-selected', opt);
        return sel ? sel.getAttribute('data-option-value') : null;
      });

      // Find option index for Color or Base Color
      this.colorOptionIndex = -1;
      $$('.pdp-variant', picker).forEach((opt, idx) => {
        const label = $('.eyebrow', opt)?.textContent?.toLowerCase() || '';
        if (label.includes('color')) {
          this.colorOptionIndex = idx;
        }
      });
      if (this.colorOptionIndex === -1 && this.currentOptions.length > 0) {
        this.colorOptionIndex = 0;
      }

      picker.addEventListener('click', (e) => {
        const pill = e.target.closest('.pdp-variant__pill');
        if (!pill) return;
        const optionEl = pill.closest('.pdp-variant');
        const optIdx = parseInt(optionEl.getAttribute('data-option-index'), 10);
        const value = pill.getAttribute('data-option-value');

        // Update internal state
        this.currentOptions[optIdx] = value;

        // Update UI
        $$('.pdp-variant__pill', optionEl).forEach(p => {
          const on = p === pill;
          p.classList.toggle('is-selected', on);
          p.setAttribute('aria-checked', String(on));
        });
        const valLabel = $('[data-selected-option-value]', optionEl);
        if (valLabel) valLabel.textContent = value;

        this._refreshVariant();
      });

      // Initial filter on load
      this._refreshVariant();
    },

    _refreshVariant() {
      const v = this.variants.find(v => v.options.every((opt, i) => opt === this.currentOptions[i]));
      if (!v) {
        // Selected option combination maps to no variant — block purchase so a
        // stale/previous variant id can't be added to cart.
        const idInput = $('[data-pdp-variant-id]');
        if (idInput) idInput.value = '';
        const atc = $('[data-pdp-atc]');
        const atcLabel = $('[data-pdp-atc-label]');
        if (atc) atc.disabled = true;
        if (atcLabel) atcLabel.textContent = 'Unavailable';
        const stickyAdd = $('[data-pdp-sticky-add]');
        if (stickyAdd) {
          stickyAdd.disabled = true;
          stickyAdd.removeAttribute('data-variant-id');
          stickyAdd.textContent = 'Unavailable';
        }
        return;
      }

      // Update form variant id
      const idInput = $('[data-pdp-variant-id]');
      if (idInput) idInput.value = v.id;

      // Update price display
      const priceEl = $('[data-pdp-price]');
      if (priceEl) priceEl.innerHTML = this._priceHTML(v);

      // Update ATC button
      const atc = $('[data-pdp-atc]');
      const atcLabel = $('[data-pdp-atc-label]');
      if (atc && atcLabel) {
        atc.disabled = !v.available;
        if (v.available) {
          atcLabel.innerHTML = 'Add to cart - <span class="spec-value" data-pdp-atc-price>' + Money.format(v.price) + '</span>';
        } else {
          atcLabel.textContent = 'Sold out';
        }
      }

      // Sticky ATC
      const stickyAdd   = $('[data-pdp-sticky-add]');
      const stickyPrice = $('[data-pdp-sticky-price]');
      if (stickyAdd) {
        stickyAdd.setAttribute('data-variant-id', v.id);
        stickyAdd.disabled = !v.available;
        stickyAdd.textContent = v.available ? 'Add to cart' : 'Sold out';
      }
      if (stickyPrice) stickyPrice.textContent = Money.format(v.price);

      // Filter gallery images by color variant
      if (this.colorOptionIndex !== -1) {
        const selectedColor = this.currentOptions[this.colorOptionIndex];
        if (selectedColor) {
          PDPGallery.filterImages(selectedColor);
        }
      }

      // Featured image swap
      if (v.featured_media && v.featured_media.id) {
        PDPGallery.showVariantImage(v.featured_media.id);
      }

      // Update URL with variant param (no reload)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url.toString());
      } catch (e) {}

      document.dispatchEvent(new CustomEvent('tl:pdp:variant_picker:change', { detail: v }));
    },

    _priceHTML(v) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        return '<span class="price price--sale">' +
          '<span class="price__sale spec-value">' + Money.format(v.price) + '</span>' +
          '<span class="price__compare spec-value"><s>' + Money.format(v.compare_at_price) + '</s></span>' +
          '</span>';
      }
      return '<span class="price"><span class="price__regular spec-value">' + Money.format(v.price) + '</span></span>';
    }
  };

  /* ---------- PDP form (qty stepper + AJAX submit) ---------- */
  const PDPForm = {
    init() {
      const form = $('[data-pdp-form]');
      if (!form) return;

      const qty = $('[data-pdp-qty]', form);
      const dec = $('[data-pdp-qty-dec]', form);
      const inc = $('[data-pdp-qty-inc]', form);
      if (dec) dec.addEventListener('click', () => { qty.value = Math.max(1, parseInt(qty.value || '1', 10) - 1); });
      if (inc) inc.addEventListener('click', () => { qty.value = parseInt(qty.value || '1', 10) + 1; });

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const id = parseInt($('[data-pdp-variant-id]', form).value, 10);
        const quantity = parseInt(qty.value || '1', 10);
        const atc = $('[data-pdp-atc]', form);

        // Collect any checked add-on products from the PDP add-ons box
        const addonInputs = $$('[data-pdp-addon]:checked');
        const items = [{ id: id, quantity: quantity }];
        addonInputs.forEach(input => {
          const addonId = parseInt(input.getAttribute('data-variant-id'), 10);
          if (addonId && addonId !== id) items.push({ id: addonId, quantity: 1 });
        });

        if (atc.getAttribute('aria-busy') === 'true') return; // block double-submit
        atc.setAttribute('aria-busy', 'true');
        const request = items.length > 1
          ? Cart.addMultiple(items)
          : Cart.add(id, quantity);
        request
          .then(() => { addonInputs.forEach(i => { i.checked = false; }); })
          .catch(err => announce(err.message))
          .finally(() => atc.removeAttribute('aria-busy'));
      });

      // Reflect total ATC price (main variant + checked add-ons) in real time
      const recalcAddonsTotal = () => {
        const priceEl = $('[data-pdp-atc-price]', form);
        if (!priceEl) return;
        const basePriceEl = $('[data-pdp-price] .price__sale, [data-pdp-price] .price__regular');
        const baseStr = basePriceEl ? basePriceEl.textContent : priceEl.textContent;
        const parsed = (baseStr || '').match(/[\d,.]+/);
        if (!parsed) return;
        const base = Math.round(parseFloat(parsed[0].replace(/,/g, '')) * 100);
        const qtyVal = Math.max(1, parseInt(qty.value || '1', 10));
        let total = base * qtyVal;
        $$('[data-pdp-addon]:checked').forEach(input => {
          const cents = parseInt(input.getAttribute('data-price') || '0', 10);
          total += cents;
        });
        if (typeof Money !== 'undefined' && Money.format) priceEl.textContent = Money.format(total);
      };
      $$('[data-pdp-addon]').forEach(input => input.addEventListener('change', recalcAddonsTotal));
      if (qty) qty.addEventListener('change', recalcAddonsTotal);
      if (dec) dec.addEventListener('click', recalcAddonsTotal);
      if (inc) inc.addEventListener('click', recalcAddonsTotal);
      // Variant picker rebuilds the ATC label; reapply addon totals afterward.
      document.addEventListener('tl:pdp:variant_picker:change', () => {
        // Defer until the variant picker's DOM updates have settled.
        requestAnimationFrame(recalcAddonsTotal);
      });

      // Sticky ATC click
      const sticky = $('[data-pdp-sticky-add]');
      if (sticky) {
        sticky.addEventListener('click', () => {
          const id = parseInt(sticky.getAttribute('data-variant-id'), 10);
          if (!id || sticky.disabled) return;
          if (sticky.getAttribute('aria-busy') === 'true') return; // block double-submit
          sticky.setAttribute('aria-busy', 'true');
          Cart.add(id, 1)
            .catch(err => announce(err.message))
            .finally(() => sticky.removeAttribute('aria-busy'));
        });
      }
    }
  };

  /* ---------- PDP sticky ATC observer ---------- */
  const PDPStickyATC = {
    init() {
      const primary = $('[data-pdp-atc]');
      const sticky  = $('[data-pdp-sticky-atc]');
      if (!primary || !sticky) return;
      // Only on small screens
      const mq = window.matchMedia('(max-width: 768px)');
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (!mq.matches) { sticky.setAttribute('hidden', ''); return; }
          if (e.intersectionRatio < 0.1) sticky.removeAttribute('hidden');
          else sticky.setAttribute('hidden', '');
        });
      }, { threshold: [0, 0.1] });
      io.observe(primary);
    }
  };

  /* ---------- PDP lifestyle filter ---------- */
  const PDPLifestyle = {
    init() {
      const root = $('[data-pdp-lifestyle]');
      if (!root) return;
      const filters = $$('[data-filter]', root);
      const items   = $$('.pdp-lifestyle__item', root);
      filters.forEach(btn => {
        btn.addEventListener('click', () => {
          filters.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          const filter = btn.getAttribute('data-filter');
          items.forEach(it => {
            const tag = it.getAttribute('data-tag');
            const show = filter === 'all' || tag === filter;
            it.style.display = show ? '' : 'none';
          });
        });
      });
    }
  };

  /* ---------- PDP customizer panel ---------- */
  const PDPCustomizer = {
    init() {
      const root = $('customize-panel');
      if (!root) return;

      const allowedOptions = (root.getAttribute('data-allowed-options') || '').split(',').map(s => s.trim().toLowerCase());
      const monogramVariantId = root.getAttribute('data-monogram-variant-id');
      const monogramPrice = parseInt(root.getAttribute('data-monogram-price') || '0', 10);
      const bumperPrice = parseInt(root.getAttribute('data-bumper-price') || '0', 10);

      // DOM Elements
      const drawer = $('.customize-panel__drawer', root);
      const backdrop = $('.customize-panel__backdrop', root);
      const closeBtns = $$('[data-customize-panel-close]', root);
      const backBtn = $('[data-customize-panel-back]', root);
      const triggerBtns = $$('[data-open-customize-panel]');
      
      const step1 = $('[data-step="1"]', root);
      const step2 = $('[data-step="2"]', root);
      const stepTitle = $('[data-step-title]', root);
      const actionBtn = $('[data-step-action]', root);
      const actionLabel = $('[data-step-action-label]', root);
      const actionPrice = $('[data-step-action-price]', root);
      const priceVal = $('[data-price]', root);
      const acknowledge = $('[data-acknowledge]', root);
      const acknowledgeCheckbox = $('[data-acknowledge-checkbox]', root);

      const viewIndicator = $('[data-view-indicator]', root);
      const viewBtns = $$('[data-preview-view]', root);
      const previewImg = $('[data-customize-preview-img]', root);
      const previewBaseUrl = previewImg ? previewImg.getAttribute('data-preview-base-url') : '';

      const monogramInput = $('[data-monogram-input]', root);

      // State
      let currentStep = 1;
      let selectedBase = $('.variant-picker__swatch.is-active[data-option-position="1"]', root)?.getAttribute('data-option-value') || '';
      let selectedBaseId = $('.variant-picker__swatch.is-active[data-option-position="1"]', root)?.getAttribute('data-variant-id') || '';
      let selectedBasePrice = parseInt($('.variant-picker__swatch.is-active[data-option-position="1"]', root)?.getAttribute('data-variant-price') || '0', 10);
      
      let selectedBumper = 'None';
      let selectedBumperId = '';

      let monogramText = '';

      // Open/Close
      const open = () => {
        root.removeAttribute('hidden');
        backdrop.classList.remove('hidden');
        requestAnimationFrame(() => {
          backdrop.style.opacity = '1';
          drawer.classList.remove('translate-x-full');
          drawer.removeAttribute('inert');
          focusTrap(drawer);
        });
      };

      const close = () => {
        backdrop.style.opacity = '0';
        drawer.classList.add('translate-x-full');
        drawer.setAttribute('inert', '');
        setTimeout(() => {
          backdrop.classList.add('hidden');
          root.setAttribute('hidden', '');
        }, 300);
      };

      triggerBtns.forEach(btn => btn.addEventListener('click', open));
      closeBtns.forEach(btn => btn.addEventListener('click', close));
      backdrop.addEventListener('click', close);

      // Swatch handlers (Base speaker color)
      const baseSwatches = $$('.variant-picker__swatch[data-option-position="1"]', root);
      baseSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          baseSwatches.forEach(s => {
            s.classList.remove('is-active');
            s.setAttribute('aria-checked', 'false');
          });
          swatch.classList.add('is-active');
          swatch.setAttribute('aria-checked', 'true');
          
          selectedBase = swatch.getAttribute('data-option-value');
          selectedBaseId = swatch.getAttribute('data-variant-id');
          selectedBasePrice = parseInt(swatch.getAttribute('data-variant-price') || '0', 10);
          
          $('[data-selected-base-value]', root).textContent = selectedBase;
          updatePreview();
          updatePrice();
        });
      });

      // Swatch handlers (Bumper)
      const bumperSwatches = $$('.variant-picker__swatch[data-bumper-value]', root);
      bumperSwatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          bumperSwatches.forEach(s => {
            s.classList.remove('is-active');
            s.setAttribute('aria-checked', 'false');
          });
          swatch.classList.add('is-active');
          swatch.setAttribute('aria-checked', 'true');
          
          selectedBumper = swatch.getAttribute('data-bumper-value');
          selectedBumperId = swatch.getAttribute('data-bumper-variant-id');
          
          $('[data-selected-bumper-value]', root).textContent = selectedBumper;
          updatePreview();
          updatePrice();
        });
      });

      // Monogram Input handler
      if (monogramInput) {
        monogramInput.addEventListener('input', () => {
          monogramText = monogramInput.value.trim().toUpperCase();
          if (monogramText.length > 0) {
            acknowledge.removeAttribute('hidden');
          } else {
            acknowledge.setAttribute('hidden', '');
            acknowledgeCheckbox.checked = false;
          }
          updatePrice();
        });
      }

      // Front/Back preview toggle
      let activeView = 'front';
      viewBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          viewBtns.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          activeView = btn.getAttribute('data-preview-view');
          
          if (activeView === 'back') {
            viewIndicator.style.transform = 'translateX(100%)';
          } else {
            viewIndicator.style.transform = 'translateX(0)';
          }
          updatePreview();
        });
      });

      const updatePreview = () => {
        if (!previewImg) return;
        if (!previewBaseUrl) {
          // No render service configured: preview with the selected variant's image.
          const active = $('.variant-picker__swatch.is-active[data-option-position="1"]', root);
          const img = active && active.getAttribute('data-variant-image');
          if (img) previewImg.src = img;
          return;
        }
        const baseName = selectedBase.toLowerCase().replace(/\s+/g, '-');
        const bumperName = selectedBumper === 'None' ? 'black' : selectedBumper.toLowerCase().replace(/\s+/g, '-');

        let url = `${previewBaseUrl}&variant=${baseName}-${bumperName}&view=${activeView}`;
        if (activeView === 'back' && monogramText) {
          url += `&text=${encodeURIComponent(monogramText)}`;
        }
        previewImg.src = url;
      };

      const calculateTotal = () => {
        let total = selectedBasePrice;
        if (selectedBumper && selectedBumper !== 'None') {
          total += bumperPrice;
        }
        if (monogramText) {
          total += monogramPrice;
        }
        return total;
      };

      const updatePrice = () => {
        const total = calculateTotal();
        if (priceVal) priceVal.textContent = Money.format(total);
      };

      // Step toggle logic
      const setStep = (step) => {
        currentStep = step;
        if (step === 1) {
          step1.removeAttribute('hidden');
          step2.setAttribute('hidden', '');
          backBtn.setAttribute('hidden', '');
          stepTitle.textContent = '1 of 2: Customize';
          actionLabel.textContent = 'Review Selection';
          actionPrice.setAttribute('hidden', '');
        } else {
          step1.setAttribute('hidden', '');
          step2.removeAttribute('hidden');
          backBtn.removeAttribute('hidden');
          stepTitle.textContent = '2 of 2: Review';
          actionLabel.textContent = selectedBaseId ? 'Add to Cart' : 'Select Color';
          actionPrice.removeAttribute('hidden');
          
          $$('[data-review-base-value]', root).forEach(el => el.textContent = selectedBase);
          const bumperReview = $('[data-review-bumper-value]', root);
          if (bumperReview) bumperReview.textContent = selectedBumper;
          
          const monogramReview = $('[data-review-monogram]', root);
          const monogramReviewVal = $('[data-review-monogram-value]', root);
          if (monogramText) {
            monogramReview?.removeAttribute('hidden');
            if (monogramReviewVal) monogramReviewVal.textContent = monogramText;
          } else {
            monogramReview?.setAttribute('hidden', '');
          }
          
          updatePrice();
        }
      };

      backBtn.addEventListener('click', () => setStep(1));

      actionBtn.addEventListener('click', () => {
        if (currentStep === 1) {
          setStep(2);
        } else {
          if (monogramText && !acknowledgeCheckbox.checked) {
            announce('Please check the box to acknowledge ship time for monogram orders.');
            alert('Please acknowledge the 1-2 week ship time for monogrammed orders.');
            return;
          }
          
          let items = [];
          
          let props = {};
          if (monogramText) props['Monogram'] = monogramText;
          if (selectedBumper && selectedBumper !== 'None') props['Bumper Color'] = selectedBumper;
          
          items.push({
            id: parseInt(selectedBaseId, 10),
            quantity: 1,
            properties: props
          });
          
          if (selectedBumper && selectedBumper !== 'None' && selectedBumperId) {
            items.push({
              id: parseInt(selectedBumperId, 10),
              quantity: 1
            });
          }
          
          if (monogramText && monogramVariantId) {
            items.push({
              id: parseInt(monogramVariantId, 10),
              quantity: 1
            });
          }
          
          if (actionBtn.getAttribute('aria-busy') === 'true') return; // block double-submit
          actionBtn.setAttribute('aria-busy', 'true');

          Cart.addMultiple(items)
            .then(() => {
              close();
              setStep(1);
              if (monogramInput) monogramInput.value = '';
              monogramText = '';
              acknowledgeCheckbox.checked = false;
              acknowledge.setAttribute('hidden', '');
            })
            .catch(err => {
              console.error(err);
              announce(err.message || 'Error adding customized speaker');
            })
            .finally(() => {
              actionBtn.removeAttribute('aria-busy');
            });
        }
      });

      // Synchronize customizer color with standard variant picker color
      document.addEventListener('tl:pdp:variant_picker:change', (e) => {
        const variant = e.detail;
        if (variant) {
          const matchingSwatch = $(`.variant-picker__swatch[data-variant-id="${variant.id}"]`, root);
          if (matchingSwatch) {
            // Clicking swatch without dispatch loops to avoid infinite event dispatching
            baseSwatches.forEach(s => {
              s.classList.remove('is-active');
              s.setAttribute('aria-checked', 'false');
            });
            matchingSwatch.classList.add('is-active');
            matchingSwatch.setAttribute('aria-checked', 'true');
            selectedBase = matchingSwatch.getAttribute('data-option-value');
            selectedBaseId = matchingSwatch.getAttribute('data-variant-id');
            selectedBasePrice = parseInt(matchingSwatch.getAttribute('data-variant-price') || '0', 10);
            $('[data-selected-base-value]', root).textContent = selectedBase;
            updatePreview();
            updatePrice();
          }
        }
      });
      
      updatePrice();
    }
  };

  /* ---------- PDP bottom bar ---------- */
  const PDPBottomBar = {
    init() {
      const bar = $('product-bottom-bar');
      if (!bar) return;

      const scrollContainer = $('[data-swatches-scroll]', bar);
      const prevBtn = $('[data-swatches-prev]', bar);
      const nextBtn = $('[data-swatches-next]', bar);
      const checkBtn = $('[data-bar-checkout]', bar);
      const barPrice = $('[data-bar-price]', bar);
      const swatches = $$('.variant-picker__swatch', bar);

      // Toggle visibility based on primary ATC scroll state
      const primaryATC = $('[data-pdp-atc]');
      if (primaryATC) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach(e => {
            const scrolledPast = e.intersectionRatio < 0.1 && e.boundingClientRect.top < 0;
            bar.classList.toggle('is-visible', scrolledPast);
          });
        }, { threshold: [0, 0.1] });
        io.observe(primaryATC);
      }

      // Swatches carousel scroll handlers
      if (scrollContainer && prevBtn && nextBtn) {
        const updateArrows = () => {
          const l = scrollContainer.scrollLeft;
          const max = scrollContainer.scrollWidth - scrollContainer.clientWidth;
          prevBtn.hidden = l <= 4;
          nextBtn.hidden = l >= max - 4;
        };

        setTimeout(updateArrows, 100);

        scrollContainer.addEventListener('scroll', throttle(updateArrows, 50));
        prevBtn.addEventListener('click', () => {
          scrollContainer.scrollBy({ left: -120, behavior: 'smooth' });
        });
        nextBtn.addEventListener('click', () => {
          scrollContainer.scrollBy({ left: 120, behavior: 'smooth' });
        });
        window.addEventListener('resize', debounce(updateArrows, 100));
      }

      // Swatch clicks (variant selections)
      swatches.forEach(sw => {
        sw.addEventListener('click', () => {
          swatches.forEach(s => s.classList.remove('is-active'));
          sw.classList.add('is-active');

          const variantId = sw.getAttribute('data-variant-id');
          const variantPrice = parseInt(sw.getAttribute('data-variant-price') || '0', 10);
          const available = sw.getAttribute('data-variant-avail') === 'true';

          const mainPicker = $('[data-variant-picker]');
          if (mainPicker) {
            const val = sw.getAttribute('data-option-value');
            const matchingPill = $(`.pdp-variant__pill[data-option-value="${val}"]`, mainPicker);
            if (matchingPill) {
              matchingPill.click();
            }
          }

          if (checkBtn) {
            checkBtn.setAttribute('data-variant-id', variantId);
            checkBtn.disabled = !available;
            const textEl = $('span', checkBtn);
            if (textEl) textEl.textContent = available ? 'Add to cart' : 'Sold Out';
          }
          if (barPrice) barPrice.textContent = Money.format(variantPrice);
        });
      });

      if (checkBtn) {
        checkBtn.addEventListener('click', () => {
          const id = parseInt(checkBtn.getAttribute('data-variant-id'), 10);
          if (!id || checkBtn.disabled) return;
          if (checkBtn.getAttribute('aria-busy') === 'true') return; // block double-submit
          checkBtn.setAttribute('aria-busy', 'true');
          Cart.add(id, 1)
            .catch(err => announce(err.message))
            .finally(() => checkBtn.removeAttribute('aria-busy'));
        });
      }

      document.addEventListener('tl:pdp:variant_picker:change', (e) => {
        const variant = e.detail;
        if (variant) {
          const sw = $(`.variant-picker__swatch[data-variant-id="${variant.id}"]`, bar);
          if (sw) {
            swatches.forEach(s => s.classList.remove('is-active'));
            sw.classList.add('is-active');
            
            if (checkBtn) {
              checkBtn.setAttribute('data-variant-id', variant.id);
              checkBtn.disabled = !variant.available;
              const textEl = $('span', checkBtn);
              if (textEl) textEl.textContent = variant.available ? 'Add to cart' : 'Sold Out';
            }
            if (barPrice) barPrice.textContent = Money.format(variant.price);
          }
        }
      });
    }
  };

  /* ---------- Wire Phase 5 inits ---------- */
  const initPhase5 = () => {
    PDPGallery.init();
    PDPVariantPicker.init();
    PDPForm.init();
    PDPStickyATC.init();
    PDPLifestyle.init();
    PDPCustomizer.init();
    PDPBottomBar.init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhase5);
  } else {
    initPhase5();
  }

  /* =========================================================================
     PHASE 6 - Article table of contents (long-form blog guides)
     ========================================================================= */

  /* ---------- ArticleTOC (auto-built from H2s in article body) ---------- */
  const ArticleTOC = {
    init() {
      const body = $('.article-page__body');
      if (!body) return;
      const headings = $$('h2', body);
      // Only worth a TOC for genuinely long guides.
      if (headings.length < 3) return;

      const slugify = (text) => text.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, 60) || 'section';

      const nav = document.createElement('nav');
      nav.className = 'article-toc';
      nav.setAttribute('aria-label', 'On this page');

      const list = document.createElement('ol');
      list.className = 'article-toc__list';
      const used = new Set();

      headings.forEach((h) => {
        let id = h.id;
        if (!id) {
          id = slugify(h.textContent);
          let unique = id, n = 2;
          while (used.has(unique) || document.getElementById(unique)) { unique = id + '-' + n++; }
          id = unique;
          h.id = id;
        }
        used.add(id);
        const li = document.createElement('li');
        li.className = 'article-toc__item';
        const a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = h.textContent;
        li.appendChild(a);
        list.appendChild(li);
      });

      const title = document.createElement('p');
      title.className = 'article-toc__title eyebrow eyebrow--muted';
      title.textContent = 'On this page';
      nav.appendChild(title);
      nav.appendChild(list);
      body.prepend(nav);
    }
  };

  const initPhase6 = () => {
    ArticleTOC.init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhase6);
  } else {
    initPhase6();
  }

  /* =========================================================================
     PHASE 7 - Product card color swatches (Turtlebox-style merchandising)
     ========================================================================= */

  /* ---------- CardSwatches (on-card color swap + quick-add sync) ----------
     Markup contract (see snippets/product-card.liquid):
       [data-product-card]                          card root
         [data-card-swatch]                         swatch button, carrying:
           data-variant-id      first available variant for that color
           data-variant-avail   "true" | "false"
           data-variant-image   variant featured image URL (width 640), optional
         .product-card__media > picture > img       responsive-image output
         .product-card__quick[data-ajax-add]        direct-add button (single
                                                    variant or color-only), with
                                                    data-label-add / data-label-soldout
     Delegated on document so it survives Customizer section re-renders and
     works in every section that renders product-card (featured grid,
     collection grid, recommendations). */
  const CardSwatches = {
    _bound: false,
    init() {
      if (this._bound) return;
      this._bound = true;
      document.addEventListener('click', (e) => {
        const swatch = e.target.closest('[data-card-swatch]');
        if (!swatch) return;
        // Card bodies sit inside/next to link wrappers — never navigate.
        e.preventDefault();
        e.stopPropagation();
        const card = swatch.closest('[data-product-card]');
        if (!card) return;

        // (a) Active state, scoped to this card only.
        $$('[data-card-swatch]', card).forEach(s => {
          const on = s === swatch;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-pressed', String(on));
        });

        // (b) Swap the card's main image. Same <picture> problem PDPGallery
        // solves: the WebP <source> elements outrank img.src, so drop
        // them and clear srcset/sizes before pointing src at the variant image.
        const imgUrl = swatch.getAttribute('data-variant-image');
        if (imgUrl) {
          const picture = $('.product-card__media > picture', card);
          const img = picture ? $('img', picture) : null;
          if (img) {
            $$('source', picture).forEach(source => source.remove());
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = imgUrl;
            // Hover (secondary) image still shows the original colorway —
            // suppress it once a swatch has been chosen (CSS handles it).
            card.classList.add('is-swatch-selected');
          }
        }

        // (c) Sync the direct-add button when present. Multi-option products
        // keep their Quick Shop modal trigger untouched (no data-ajax-add).
        const quick = $('.product-card__quick[data-ajax-add]', card);
        if (quick) {
          const avail = swatch.getAttribute('data-variant-avail') === 'true';
          quick.setAttribute('data-variant-id', swatch.getAttribute('data-variant-id') || '');
          quick.disabled = !avail;
          quick.textContent = avail
            ? (quick.getAttribute('data-label-add') || 'Add to cart')
            : (quick.getAttribute('data-label-soldout') || 'Sold out');
        }
      });
    }
  };

  const initPhase7 = () => {
    CardSwatches.init();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPhase7);
  } else {
    initPhase7();
  }

/* ---------- Expose ---------- */
  window.TL = {
    Cart, Drawer, Modal, Reveal, Tabs, Accordion, SupportTabs, Carousel, StackedSlider, Header, MLTeaser,
    Announcement, MegaNav, Search, Money, CartUI,
    QuickShop, CollectionFilters, CardSwatches,
    PDPGallery, PDPVariantPicker, PDPForm, PDPStickyATC, PDPLifestyle, PDPCustomizer, PDPBottomBar,
    ArticleTOC,
    _utils: { $, $$, debounce, throttle, announce, focusTrap }
  };
})();
